<template>
  <el-dialog
    :model-value="visible"
    :class="{
      'dex-modal-video': !$device.isMobile,
      'dex-modal-video-mobile': $device.isMobile
    }"
    destroy-on-close
    :show-close="false"
    :lock-scroll="true"
    :fullscreen="$device.isMobile"
    @update:model-value="$emit('update:visible', $event)"
    @close="handleClose"
  >
    <div v-if="!$device.isMobile" class="dex-video-bg">
      <img v-if="!videoElementLoaded" src="/img/dex/dex-bg.webp" class="video-placeholder" />
      <video v-show="videoElementLoaded" ref="refVideo" muted loop playsinline preload="metadata" width="100%">
        <source src="/assets/video/dex.mp4" type="video/mp4" />
      </video>
    </div>

    <Transition name="fade-token" mode="out-in">
      <img
        v-if="tokenB.key && footerAssets.includes(tokenB.key as Tokens)"
        :key="tokenB.key"
        class="footer-image"
        :src="
          $device.isMobile
            ? `/img/dex/mobile/${isSellTokenA ? tokenB.key : tokenA.key}.png`
            : `/img/dex/${isSellTokenA ? tokenB.key : tokenA.key}.png`
        "
      />
    </Transition>

    <el-dialog
      :model-value="visible"
      class="dex-modal"
      :class="{ 'ru-locale': $i18n.locale === 'ru', 'dex-modal-mobile': $device.isMobile }"
      :fullscreen="$device.isMobile"
      destroy-on-close
      :modal="false"
      :close-on-click-modal="false"
      @update:model-value="$emit('update:visible', $event)"
      @close="handleClose"
    >
      <div
        class="dex-modal-content"
        :class="{
          'dex-modal-content-mobile': $device.isMobile
        }"
      >
        <div class="dex-title">{{ $t('dexTitle') }}</div>

        <div class="segment-container-wrapper">
          <el-skeleton :loading="isDexDataLoading" animated>
            <template #template>
              <div class="token-segment-skeleton">
                <el-skeleton-item class="token-skeleton" />
                <el-skeleton-item class="token-skeleton" />
                <el-skeleton-item class="token-skeleton" />
                <el-skeleton-item class="token-skeleton" />
                <el-skeleton-item class="token-skeleton" />
              </div>
            </template>
            <template #default>
              <div class="segmented-scroll-container">
                <el-segmented
                  v-model="tokenB.key"
                  :class="{
                    'segmented-mobile': $device.isMobile
                  }"
                  :options="tokenOptionsForSegmented"
                  @change="changeToken"
                >
                  <template #default="scope">
                    <div class="segment-container">
                      <img :src="scope.item.icon" />
                    </div>
                  </template>
                </el-segmented>
              </div>
            </template>
          </el-skeleton>
        </div>

        <div class="exchange-form">
          <div class="swap-container" :class="{ animating: isAnimating, swapped: isSwapped }">
            <div class="input-container-a">
              <div class="input-header">
                <span v-if="isSellTokenA" class="input-header-right">{{ $t('dexTitlePay') }}</span>
                <span v-else class="input-header-right">{{ $t('dexTitleReceive') }}</span>

                <UIBalance
                  v-if="address"
                  :key="balanceTokenAKey"
                  :token-address="tokenATokenAddress"
                  :label="$t('coreAvailable')"
                  :formatter="formatInputWithSpaces"
                  :style="{ cursor: isSellTokenA ? 'pointer' : 'default' }"
                  @click="setMaxBalanceA"
                />
              </div>

              <el-skeleton :loading="isRateTokenALoading" animated>
                <template #template>
                  <el-skeleton-item style="width: 60%; height: 35px" class="input-skeleton" />
                </template>
              </el-skeleton>

              <el-input
                v-model="tokenA.amount"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                spellcheck="false"
                class="input-with-select"
                :class="{
                  'input-with-select-loading': true,
                  'input-font-size-16': tokenA.amount.length >= 10,
                  'is-error': tokenAExceedsBalance && isSellTokenA
                }"
                :disabled="!isSellTokenA"
                :formatter="formatInputWithSpaces"
                :parser="parseInputSpaces"
                @keydown="handleTokenAKeydown"
                @keyup="handleTokenAKeyup"
                @input="handleTokenAInput"
              >
                <template #prepend>
                  <el-skeleton :loading="isDexDataLoading" animated class="select-skeleton">
                    <template #template>
                      <el-skeleton-item style="height: 35px; width: 130px" />
                    </template>

                    <template #default>
                      <el-select
                        v-model="tokenA.key"
                        :style="{ width: '140px' }"
                        :suffix-icon="CaretBottom"
                        class="input-with-select-hide-icon"
                        popper-class="select-popper"
                        :disabled="true"
                        automatic-dropdown
                        placement="bottom-start"
                        :fallback-placement="['bottom-start', 'top-start']"
                        :popper-options="{ offset: [0, -5] }"
                        @change="changeToken"
                      >
                        <template #prefix>
                          <img :src="tokenA.icon" class="input-with-select-image usdt-padding" />
                        </template>

                        <template #label>{{ tokenA.label }}</template>

                        <template #default>
                          <div class="dex-options">
                            <el-option
                              v-for="(item, index) in tokenAOptions"
                              :key="index"
                              :label="item.label"
                              :value="item.key"
                              class="option-dex"
                            >
                              <img :src="item.icon" alt="" />
                              <span>{{ item.label }}</span>
                            </el-option>
                          </div>
                        </template>
                      </el-select>
                    </template>
                  </el-skeleton>
                </template>
              </el-input>

              <div v-if="isSellTokenA" class="slider-container">
                <span> {{ $t('coreMinimum') }} </span>
                <el-slider
                  v-model="tokenASliderValue"
                  :min="tokenAMin"
                  :max="tokenAMax"
                  :show-tooltip="false"
                  :step="INPUT_STEP"
                  class="slider-default"
                />
                <span style="cursor: pointer" @click="setMaxBalanceA"> {{ $t('coreMax') }} </span>
              </div>
            </div>

            <div class="swap-button-container">
              <button class="swap-button" :disabled="isAnimating" @click="swapTokens" />
              <hr />
            </div>

            <div class="input-container-b">
              <div class="input-header">
                <span v-if="isSellTokenA" class="input-header-right">{{ $t('dexTitleReceive') }}</span>
                <span v-else class="input-header-right">{{ $t('dexTitlePay') }}</span>

                <UIBalance
                  v-if="address"
                  :key="balanceTokenBKey"
                  :token-address="tokenBTokenAddress"
                  :label="$t('coreAvailable')"
                  :formatter="formatInputWithSpaces"
                  :style="{ cursor: !isSellTokenA ? 'pointer' : 'default' }"
                  @click="setMaxBalanceB"
                />
              </div>

              <el-skeleton :loading="isRateTokenBLoading" animated>
                <template #template>
                  <el-skeleton-item style="width: 210px; height: 35px" class="input-skeleton" />
                </template>
              </el-skeleton>

              <el-input
                v-model="tokenB.amount"
                class="input-with-select"
                :class="{
                  'input-font-size-16': tokenB.amount.length >= 10 && !$device.isMobile,
                  'input-font-size-14': tokenB.amount.length >= 10 && $device.isMobile,
                  'is-error': tokenBExceedsBalance && !isSellTokenA
                }"
                :disabled="isSellTokenA"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                spellcheck="false"
                :formatter="formatInputWithSpaces"
                :parser="parseInputSpaces"
                @keydown="handleTokenBKeydown"
                @keyup="handleTokenBKeyup"
                @input="handleTokenBInput"
              >
                <template #prepend>
                  <el-skeleton :loading="isDexDataLoading" animated class="select-skeleton">
                    <template #template>
                      <el-skeleton-item style="height: 35px; width: 130px" />
                    </template>
                    <template #default>
                      <el-select
                        v-model="tokenB.key"
                        :style="{ width: '180px' }"
                        popper-class="select-popper"
                        :suffix-icon="CaretBottom"
                        automatic-dropdown
                        placement="bottom-start"
                        :fallback-placement="['bottom-start', 'top-start']"
                        :popper-options="{ offset: [0, -5] }"
                        :disabled="isAnimating"
                        @change="changeToken"
                      >
                        <template #prefix>
                          <img :src="tokenB.icon" class="input-with-select-image" />
                        </template>

                        <template #label>{{ tokenB.label }}</template>

                        <template #default>
                          <div class="dex-options">
                            <el-option
                              v-for="(item, index) in tokenBOptions"
                              :key="index"
                              :label="item.label"
                              :value="item.key"
                              class="option-dex"
                            >
                              <img :src="item.icon" alt="" />
                              <span>{{ item.label }}</span>
                              <span class="token-balance" :class="{ 'ru-locale': $i18n.locale === 'ru' }">{{
                                formatInputWithSpaces(item.balance)
                              }}</span>
                            </el-option>
                          </div>
                        </template>
                      </el-select>
                    </template>
                  </el-skeleton>
                </template>
              </el-input>

              <div v-if="!isSellTokenA" class="slider-container">
                <span> {{ $t('coreMinimum') }} </span>
                <el-slider
                  v-model="tokenBSliderValue"
                  :min="tokenBMin"
                  :max="tokenBMax"
                  :step="INPUT_STEP"
                  class="slider-default"
                  :show-tooltip="false"
                />
                <span style="cursor: pointer" @click="setMaxBalanceB"> {{ $t('coreMax') }} </span>
              </div>
            </div>

            <div v-if="!isAnimating" class="actual-rate">
              <el-skeleton
                :loading="!actualRate || isActualRateLoading"
                :throttle="{ leading: 100, trailing: 500 }"
                animated
                class="rate-skeleton"
              >
                <template #template>
                  <el-skeleton-item class="rate-skeleton-item" />
                </template>
                <template #default>
                  <div class="actual-rate-label">{{ actualRateFormatted }}</div>
                </template>
              </el-skeleton>
            </div>
          </div>

          <DexButtons
            :label="buttonLabel"
            :disabled="isButtonDisabled"
            @show-modal="handleShowConfirmModal"
            @switch-to-next="setNextToken"
            @switch-to-prev="setPrevToken"
          />
        </div>
      </div>

      <el-dialog
        v-model="showConfirmModal"
        class="dialog-default dex-confirm-modal"
        :class="{
          'ru-locale': $i18n.locale === 'ru',
          'dex-confirm-mobile': $device.isMobile
        }"
        :fullscreen="$device.isMobile"
        destroy-on-close
        center
      >
        <DexConfirmModal
          :data="exchangeData"
          :is-enough-balance="isEnoughBalance"
          :confirm-button-label="buttonLabel"
          @exchange="handleExchange"
        />
      </el-dialog>

      <el-dialog
        v-model="showSuccessModal"
        class="dialog-default dex-success-modal"
        :class="{
          'ru-locale': $i18n.locale === 'ru',
          'dex-success-mobile': $device.isMobile
        }"
        :fullscreen="$device.isMobile"
        destroy-on-close
        center
      >
        <DexSuccessModal v-if="exchangeDataPurchased" :data="exchangeDataPurchased" @close="closeSuccessModal" />
      </el-dialog>
    </el-dialog>
  </el-dialog>
</template>

<script setup lang="ts">
import { CaretBottom } from '@element-plus/icons-vue';
import DexConfirmModal from '~/components/Dex/DexConfirmModal.vue';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import DexButtons from '~/components/Dex/DexButtons.vue';
import {
  StableCoinTokens,
  TOKEN_A_AMOUNT_MIN,
  TOKEN_B_AMOUNT_MIN,
  Tokens,
  TOKENS_MAX,
  INPUT_STEP
} from '~/utils/constants';
import { BigNumber } from 'bignumber.js';
import { keydownHandler, formatInputWithSpaces, parseInputSpaces, handleInputChange } from '~/utils/inputDex';
import { useDebounceFn } from '@vueuse/core';

const props = defineProps<{
  visible: boolean;
}>();

const footerAssets = [
  Tokens.clay,
  Tokens.coal,
  Tokens.goldOre,
  Tokens.ironOre,
  Tokens.pearl,
  Tokens.sand,
  Tokens.wood,
  StableCoinTokens.usdt
];

const emit = defineEmits(['update:visible']);
const { address } = useWeb3ModalAccount();
const store = useMainStore();
const { t } = useI18n();

const videoElementLoaded = ref(false);
const refVideo = ref<HTMLVideoElement | null>(null);
const isTokenAEditing = ref(false);
const isTokenBEditing = ref(false);

const { login } = useAuth();
const {
  tokenA,
  tokenB,
  tokenA_balance,
  tokenB_balance,
  tokenATokenAddress,
  tokenBTokenAddress,
  isAnimating,
  tokenAOptions,
  tokenBOptions,
  swapTokens,
  changeToken,
  actualRate,
  actualRateFormatted,
  getActualRate,
  showSuccessModal,
  showConfirmModal,
  exchangeDataPurchased,
  exchangeData,
  exchange,
  isSellTokenA,
  isActualRateLoading,
  isRateTokenALoading,
  isRateTokenBLoading,
  isDexDataLoading,
  availableTokenBOptions,
  loadDexData,
  isSwapped,
  isEnoughBalance,
  isExchangeLoading,
  tokenOptionsForSegmented,
  closeSuccessModal,
  balanceTokenAKey,
  balanceTokenBKey,
  setMaxBalanceA,
  setMaxBalanceB
} = await useDexExchange(toRef(props, 'visible'));

const tokenAMax = computed(() => {
  if (!address.value) {
    return TOKENS_MAX;
  }
  return Math.min(tokenA_balance.value > 0 ? tokenA_balance.value : TOKENS_MAX, TOKENS_MAX);
});
const tokenBMax = computed(() => {
  if (!address.value) {
    return TOKENS_MAX;
  }
  return Math.min(tokenB_balance.value > 0 ? tokenB_balance.value : TOKENS_MAX, TOKENS_MAX);
});
const tokenAMin = computed(() => Math.min(TOKEN_A_AMOUNT_MIN, tokenAMax.value));
const tokenBMin = computed(() => Math.min(TOKEN_B_AMOUNT_MIN, tokenBMax.value));

const tokenAExceedsBalance = computed(() => {
  if (!address.value) {
    return false;
  }

  const amount = BigNumber(tokenA.amount || 0);
  const balance = BigNumber(tokenA_balance.value || 0);

  return amount.gt(balance);
});

const tokenBExceedsBalance = computed(() => {
  if (!address.value) {
    return false;
  }

  const amount = BigNumber(tokenB.amount || 0);
  const balance = BigNumber(tokenB_balance.value || 0);

  return amount.gt(balance);
});

const sellTokenExceedsBalance = computed(() => {
  return isSellTokenA.value ? tokenAExceedsBalance.value : tokenBExceedsBalance.value;
});

const isButtonDisabled = computed(() => {
  if (!address.value) {
    return false;
  }

  if (
    isExchangeLoading.value ||
    isTokenAEditing.value ||
    isTokenBEditing.value ||
    isRateTokenALoading.value ||
    isRateTokenBLoading.value ||
    isRateTokenBLoading.value ||
    sellTokenExceedsBalance.value
  ) {
    return true;
  }

  return tokenA_balance.value === 0 && tokenB_balance.value === 0;
});

const handleClose = () => {
  emit('update:visible', false);
  showConfirmModal.value = false;
  showSuccessModal.value = false;
  store.openedBuilding = null;
};

const handleShowConfirmModal = async () => {
  if (!address.value) {
    await login();
    return;
  }
  showConfirmModal.value = true;
};

onMounted(async () => {
  await loadDexData();
  await getActualRate();

  if (refVideo.value) {
    loadVideo();
  }
});

watch(refVideo, (newVideo) => {
  if (newVideo) {
    loadVideo();
  }
});

onUnmounted(() => {
  emit('update:visible', false);
});

const loadVideo = () => {
  if (!refVideo.value) {
    return;
  }

  refVideo.value.addEventListener('loadeddata', () => {
    videoElementLoaded.value = true;
    playVideo(refVideo.value!);
  });

  refVideo.value.addEventListener('error', (error) => {
    console.error('Video loading error:', error);
    videoElementLoaded.value = false;
  });
};

const playVideo = async (videoElement: HTMLVideoElement) => {
  try {
    if (videoElement.paused) {
      await videoElement.play();
    }
  } catch (error) {
    console.error('Video playback error:', error);
  }
};

const tokenAAmountNumber = computed({
  get: () => {
    if (!tokenA.amount || tokenA.amount.trim() === '') {
      return 0;
    }
    return BigNumber(tokenA.amount).toNumber();
  },
  set: (val: number) => {
    tokenA.amount = BigNumber(val).toString();
  }
});

const tokenBAmountNumber = computed({
  get: () => {
    if (!tokenB.amount || tokenB.amount.trim() === '') {
      return 0;
    }
    return BigNumber(tokenB.amount).toNumber();
  },
  set: (val: number) => {
    tokenB.amount = BigNumber(val).toString();
  }
});

const tokenASliderValue = computed({
  get: () => {
    if (!tokenA.amount || tokenA.amount.trim() === '') {
      return tokenAMin.value;
    }
    const value = tokenAAmountNumber.value;
    const clamped = Math.min(Math.max(value, tokenAMin.value), tokenAMax.value);
    return isFinite(clamped) ? clamped : tokenAMin.value;
  },
  set: (val: number) => {
    tokenAAmountNumber.value = val;
  }
});

const tokenBSliderValue = computed({
  get: () => {
    if (!tokenB.amount || tokenB.amount.trim() === '') {
      return tokenBMin.value;
    }
    const value = tokenBAmountNumber.value;
    const clamped = Math.min(Math.max(value, tokenBMin.value), tokenBMax.value);
    return isFinite(clamped) ? clamped : tokenBMin.value;
  },
  set: (val: number) => {
    tokenBAmountNumber.value = val;
  }
});

const buttonLabel = computed(() => {
  const sellToken = isSellTokenA.value ? tokenA : tokenB;
  const buyToken = isSellTokenA.value ? tokenB : tokenA;
  const isSellingToken = buyToken.key === StableCoinTokens.usdt;

  if (isSellingToken) {
    return `${t('coreSell')} ${sellToken.label}`;
  } else {
    return `${t('coreBuy')} ${buyToken.label}`;
  }
});

const handleTokenAInput = (value: string): void => {
  if (!value || value.trim() === '') {
    tokenA.amount = '';
    return;
  }
  tokenA.amount = handleInputChange(value);
};

const handleTokenBInput = (value: string): void => {
  if (!value || value.trim() === '') {
    tokenB.amount = '';
    return;
  }
  tokenB.amount = handleInputChange(value);
};

const handleTokenAKeydown = (event: Event | KeyboardEvent): void => {
  keydownHandler(event, () => {
    isTokenAEditing.value = true;
  });
};

const handleTokenAKeyup = (): void => {
  isTokenAEditing.value = false;
};

const handleTokenBKeyup = (): void => {
  isTokenBEditing.value = false;
};

const handleTokenBKeydown = (event: Event | KeyboardEvent): void => {
  keydownHandler(event, () => {
    isTokenBEditing.value = true;
  });
};

const { callBalanceUpdate } = useHalloweenStore();
const handleExchange = useDebounceFn(async () => {
  await exchange();
  callBalanceUpdate();
}, 500);

const setNextToken = () => {
  const currentIndex = availableTokenBOptions.value.findIndex((token) => token.key === tokenB.key);

  if (currentIndex !== -1) {
    const nextIndex = (currentIndex + 1) % availableTokenBOptions.value.length;
    const nextToken = availableTokenBOptions.value[nextIndex];
    changeToken(nextToken.key as Tokens);
  }
};

const setPrevToken = () => {
  const currentIndex = availableTokenBOptions.value.findIndex((token) => token.key === tokenB.key);

  if (currentIndex !== -1) {
    const prevIndex = currentIndex === 0 ? availableTokenBOptions.value.length - 1 : currentIndex - 1;
    const prevToken = availableTokenBOptions.value[prevIndex];
    changeToken(prevToken.key as Tokens);
  }
};
</script>

<style lang="scss">
$input-container-offset: 56px;
$input-container-padding: 22px;
$input-container-width: 200px;
$input-container-spacing: 60px;
$input-container-b-offset: 72px;

.el-dialog.dex-modal-video {
  width: 1130px;
  min-width: 1130px;
  padding: 0;
  height: 720px;
  border-radius: 20px;
  overflow: visible;
  border: 0.5px solid var(--blue-4);
  box-shadow: 0px 0px 30px 0px var(--blue-5);
  position: relative;

  .footer-image {
    width: 924px;
    height: 283px;
    min-height: 283px;
    position: absolute;
    left: 50%;
    bottom: -80px;
    transform: translateX(-50%);
    z-index: 10000;
  }

  @media screen and (max-width: 900px) {
    .el-overlay-dialog {
      overflow: hidden;
    }

    min-width: unset;
  }

  .el-dialog__body {
    padding: 0;
  }

  .dex-video-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 20px;
    }

    .video-placeholder {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 20px;
    }
  }
}

.el-dialog.dex-modal-video {
  .el-overlay {
    background-color: transparent;
    border-radius: 20px;
  }
}

.el-dialog.dex-modal-video-mobile {
  overflow: hidden;

  .el-overlay-dialog {
    overflow: hidden;
  }

  .el-dialog__body {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    margin: 0;
    width: 100%;
  }

  .el-dialog__header {
    padding: 0;
  }

  .footer-image {
    height: 190px;
    min-height: 190px;
    position: absolute;
    bottom: $input-container-spacing;
    left: 50%;
    transform: translateX(-50%);
  }
}

.el-dialog.dex-modal.dex-modal-mobile.is-fullscreen {
  background: transparent;
  min-width: unset;
}

.dex-modal-content.dex-modal-content-mobile {
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh);
  position: relative;

  .dex-title {
    margin-top: 35px;
  }

  .exchange-form {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .buttons-container {
    justify-content: space-between;
    width: 100%;
    margin-top: auto;
    padding: 0 20px;
    position: sticky;
    bottom: 30px;
    z-index: 10;
  }

  .swap-container {
    .input-container-a,
    .input-container-b {
      width: 100%;
    }
  }
}

.dex-modal-content:not(.dex-modal-content-mobile) {
  .buttons-container {
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
    position: absolute;
  }
}

.dex-modal-content {
  display: flex;
  flex-direction: column;

  .swap-container {
    height: 210px;
    max-height: 210px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 11px;

    .actual-rate {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      min-height: 16px;
      height: 17px;
      position: absolute;
      bottom: 6px;
      left: 21px;

      .actual-rate-label {
        font-family: Eczar;
        font-weight: 500;
        font-size: 12px;
        line-height: 100%;
        white-space: nowrap;
        color: var(--dark-6);
      }

      .rate-skeleton {
        width: $input-container-width;
        height: 12px;

        .rate-skeleton-item {
          height: 12px;
          margin-top: 1px;
        }
      }
    }

    .input-container-a {
      width: 400px;
      position: absolute;
      top: calc(50% - #{$input-container-offset} - #{$input-container-padding});
      left: 50%;
      margin-left: -$input-container-width;

      .input-label {
        display: flex;
        justify-content: space-between;

        font-family: Eczar;
        font-weight: 500;
        font-size: 12px;
        color: var(--main-text-color);
      }

      .input-with-select-image {
        width: auto;
        height: 25px;
      }

      .usdt-padding {
        padding-left: 7px;
      }
    }

    .input-container-b {
      width: 400px;
      position: absolute;
      top: calc(50% + #{$input-container-b-offset} - #{$input-container-padding});
      left: 50%;
      margin-left: -$input-container-width;

      .input-label {
        display: flex;
        justify-content: flex-end;

        font-family: Eczar;
        font-weight: 500;
        font-size: 12px;
        color: var(--main-text-color);
      }
    }

    .input-container-a,
    .input-container-b {
      .input-skeleton {
        position: absolute;
        z-index: 1;
        right: 5px;
        top: 4px;
      }

      .select-skeleton {
        position: absolute;
        left: 2px;
        top: 4px;
        z-index: 1;
      }

      .input-header {
        display: flex;
        justify-content: flex-start;

        .price-value {
          font-family: Eczar;
          font-weight: 500;
          font-size: 12px;
          line-height: 100%;
        }

        .price-value.price-value-zero {
          color: var(--error);
        }

        .balance {
          backdrop-filter: none;
          position: absolute;
          top: -14px;
          right: 0px;
          width: 150px;
          justify-content: flex-end;
        }

        .input-header-right {
          font-family: Eczar;
          font-weight: 500;
          font-size: 12px;
          line-height: 1;
          color: var(--main-text-color);
          position: absolute;
          left: 15px;
          top: -15px;
        }
      }

      @media (max-width: 479px) {
        margin: 0;
        left: 0;
      }
    }

    .slider-container {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 17px;
      margin-top: 5px;

      span {
        font-family: Eczar;
        font-weight: 600;
        font-size: 12px;
        color: var(--dark-6);
        line-height: 100%;
        text-transform: lowercase;
      }
    }

    &.animating {
      &.swapped {
        .input-container-a {
          animation: moveToBottom 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
        .input-container-b {
          animation: moveToTop 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
      }

      &:not(.swapped) {
        .input-container-a {
          animation: moveToTop 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
        .input-container-b {
          animation: moveToBottom 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
      }
    }

    &:not(.swapped) {
      .input-container-a {
        top: calc(50% - #{$input-container-spacing} - #{$input-container-padding});
        transform: none;
      }
      .input-container-b {
        top: calc(50% + #{$input-container-spacing} - #{$input-container-padding});
        transform: none;
      }
    }

    &.swapped {
      .input-container-a {
        top: calc(50% + #{$input-container-spacing} - #{$input-container-padding});
        transform: none;
      }
      .input-container-b {
        top: calc(50% - #{$input-container-spacing} - #{$input-container-padding});
        transform: none;
      }
    }
  }

  @mixin generateArcAnimation($startY, $endY, $direction: 1) {
    @for $i from 0 through 100 {
      $progress: $i * 1;
      $t: calc($i / 100);

      $currentY: $startY + ($endY - $startY) * $t;

      $xOffset: sin($t * 3.14159) * 120px * $direction;

      #{$progress}% {
        top: calc(50% + #{$currentY} - #{$input-container-padding});
        left: calc(50% + #{$xOffset});
        margin-left: calc(-#{$input-container-width} + #{$xOffset});
        transform: none;
      }
    }
  }

  @keyframes moveToBottom {
    @include generateArcAnimation(-$input-container-spacing, $input-container-spacing, 1);
  }

  @keyframes moveToTop {
    @include generateArcAnimation($input-container-spacing, -$input-container-spacing, -1);
  }

  .segment-container {
    position: relative;
  }

  .segment-container-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 64px;
    width: 100%;
  }

  .segmented-scroll-container {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
    scrollbar-color: var(--scrollbar) transparent;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--scrollbar);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: var(--scrollbar-hover);
    }

    .el-segmented {
      min-width: max-content;
      flex-shrink: 0;
      width: 100%;

      &__group {
        margin: 0 auto;
      }
    }
  }

  .token-segment-skeleton {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;

    .token-skeleton {
      width: 50px;
      height: 50px;
      border-radius: 12px;
    }
  }

  .swap-button-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 36px;
    z-index: 10;

    .swap-button {
      height: 36px;
      width: 36px;
      border: none;
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
      background-image: url('~/public/img/icons/arrowBottom.svg');
      transition: transform 0.5s;
      border-radius: 50%;
      background-color: var(--blue-7);
      position: absolute;
      z-index: 11;

      &:disabled {
        background-image: url('~/public/img/icons/arrowBottomDisabled.svg');
      }
    }

    hr {
      width: 332px;
      height: 1px;
      background-color: var(--dark-6);
      border: none;
      margin: 0;
    }
  }

  .exchange-form {
    margin-top: 10px;
  }

  .el-segmented {
    background: transparent;
  }

  .el-input {
    border-radius: 10px;
    height: 44px;
    border: solid 1px var(--dark-6);
    background-color: var(--blue-7);
  }

  .el-select__popper {
    visibility: visible !important;
  }

  .el-select__selected-item {
    font-family: Eczar;
    font-weight: 500;
    font-size: 20px;
    line-height: 1;
    color: var(--dark-6);
  }

  .input-with-select {
    .input-with-select-image {
      width: 40px;
    }
  }
}

.dex-title {
  font-family: Eczar;
  font-weight: 600;
  font-size: 32px;
  color: var(--main-text-color);
  line-height: 1;
  text-align: center;
  margin-bottom: 20px;
}
</style>

<style lang="scss">
$input-container-spacing: 60px;

.el-dialog.dex-modal {
  .el-select__wrapper.is-disabled input {
    cursor: default;
  }
}

.el-select__popper.select-popper {
  border: none;
  border-radius: 10px;
  margin-top: -8px;

  .el-select-dropdown__list {
    padding: 0;
    margin: 0;
  }

  .el-scrollbar {
    background-color: var(--blue-7);
    border: solid 1px var(--dark-6) !important;
    padding: 0;
    margin: 0;
    border-radius: 10px;
  }
}

.dex-modal-content {
  .exchange-form {
    .el-skeleton__item {
      border-radius: 8px;
    }
    .el-select__wrapper {
      padding-left: 6px;
      height: 44px;
    }

    .el-select__wrapper.is-disabled {
      padding-left: 6px;
      cursor: default;
    }

    .el-select {
      user-select: none;
      background: transparent;

      .el-select__suffix {
        transform: scale(1.3);
      }
    }

    .el-select.input-with-select-hide-icon .el-select__suffix {
      display: none;
    }

    .el-input-group__prepend {
      position: relative;
      background: transparent;
    }

    .el-select__placeholder {
      font-family: Eczar !important;
      font-weight: 500;
      font-size: 20px;
      line-height: 1;
      color: var(--dark-6);
    }

    .el-input__inner {
      font-family: Eczar;
      font-weight: 500;
      font-size: 20px;
      line-height: 1;
      text-align: right;
      color: var(--main-text-color);
    }

    .input-font-size-16 {
      .el-input__inner {
        font-size: 16px;
      }
    }

    .input-font-size-14 {
      .el-input__inner {
        font-size: 14px;
      }
    }
  }

  .el-segmented__item-selected {
    border-radius: 12px;
    background: linear-gradient(180deg, #0083ad 0%, #00354f 100%);
  }

  .el-segmented__group {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
  }

  .el-segmented__item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    height: 50px;
    width: 50px;
    max-height: 50px;
    max-width: 50px;
    padding: 0;
    border-radius: 12px;
    border: 1px solid var(--blue-8);
    background: linear-gradient(0deg, #081b25, #081b25),
      linear-gradient(103.98deg, rgba(0, 0, 0, 0) 23.92%, rgba(0, 0, 0, 0.36) 63.2%, rgba(0, 0, 0, 0) 97.87%);
    transition: height 1s;

    img {
      position: absolute;
      top: 5px;
      left: 5px;
      height: 38.5px;
      width: 38.5px;
    }
  }

  .el-segmented:not(.segmented-mobile) {
    .el-segmented__item:not(.is-disabled):not(.is-selected):hover {
      background: radial-gradient(
          80% 120% at 50% 0%,
          var(--blue-14) 0%,
          rgba(0, 131, 173, 0.6) 20%,
          rgba(0, 131, 173, 0.3) 35%,
          rgba(0, 131, 173, 0.1) 50%,
          rgba(0, 131, 173, 0.05) 65%,
          rgba(0, 131, 173, 0.02) 80%,
          rgba(0, 131, 173, 0.01) 90%,
          rgba(0, 0, 0, 0) 100%
        ),
        var(--blue-15);
    }
  }

  .el-segmented.segmented-mobile {
    .el-segmented__item:not(.is-disabled):not(.is-selected):hover {
      background: transparent;
    }
  }

  .el-segmented__item.is-selected {
    height: $input-container-spacing;
    min-height: $input-container-spacing;
    width: $input-container-spacing;
    min-width: $input-container-spacing;
    outline: none;
    -webkit-tap-highlight-color: transparent;

    img {
      height: 50px;
      width: 50px;
      top: -1px;
      left: 0px;
    }
  }

  .el-segmented__item-label {
    height: 50px;
    min-width: 50px;
    max-width: 50px;
  }

  .skeleton-input-group__prepend {
    border: none !important;
    box-shadow: none !important;
  }

  .el-select__wrapper {
    box-shadow: none !important;
    border: none;
  }

  .el-input__wrapper {
    padding-right: 14px;
    border: none !important;
    box-shadow: none !important;
    background-color: transparent;
  }

  .el-input.is-disabled {
    cursor: default;

    .el-input__wrapper {
      cursor: default;
      color: var(--main-text-color);
      background-color: transparent;
    }
  }

  .el-input.is-disabled .el-input__inner {
    cursor: default;
    color: var(--main-text-color);
    -webkit-text-fill-color: var(--main-text-color);
    opacity: 1;
    -webkit-opacity: 1;
  }

  .el-input-group__prepend {
    box-shadow: none !important;
    position: relative;

    &:after {
      display: block;
      position: absolute;
      right: 0;
      content: '';
      width: 1px;
      height: 25px;
      background: var(--dark-6);
    }
  }
}

.el-select-dropdown__item.option-dex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 400px;
  padding: 10px 14px 10px 6px;
  height: 45px;
  gap: 10px;

  @media screen and (max-width: 479px) {
    width: 95dvw;
  }

  span {
    font-family: Eczar;
    font-weight: 500;
    font-size: 20px;
    line-height: 1;
    color: var(--dark-6);
  }

  .token-balance {
    font-size: 20px;
    font-weight: 500;
    color: var(--main-text-color);
    margin-left: auto;
  }

  .token-balance.ru-locale {
    font-family: PT Serif;
  }

  img {
    height: 40px;
    width: 40px;
  }

  &.is-selected {
    color: var(--dark-6);
  }
}

.el-select-dropdown__item.option-dex:not(:last-child) {
  border-bottom: 1px solid var(--blue-10);
}

.dex-options {
  .el-select-dropdown__item.is-hovering {
    background-color: var(--blue-10);
  }

  .el-select-dropdown__item:first-child.is-hovering {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  .el-select-dropdown__item:last-child.is-hovering {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
}

.input-container-a,
.input-container-b {
  .balance {
    background: transparent;
    box-shadow: none;
    border: none;
    height: 12px;
    width: 150px;
    padding: 0 15px 5px;
    justify-content: flex-end;
    gap: 5px;
    align-items: center;
    display: flex;
    line-height: 1;

    .price-value {
      font-family: Eczar;
      font-weight: 500;
      font-size: 12px;
      line-height: 1;
      color: var(--main-text-color);
      margin: 0;
      padding: 0;
    }

    .balance-label {
      font-size: 12px;
      line-height: 1;
      margin: 0;
      padding: 0;
    }

    .skeleton {
      top: -4px;
      right: 0px;
      width: 150px;
      justify-content: flex-end;
    }

    img {
      display: none;
    }

    .balance-label {
      font-family: Eczar;
      font-weight: 400;
      font-size: 12px;
      text-transform: lowercase;
      line-height: 100%;
      color: var(--dark-6);
    }
  }
}

.el-input-number ::-webkit-outer-spin-button,
.el-input-number ::-webkit-inner-spin-button {
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
}

.el-input-number {
  appearance: textfield;
  -moz-appearance: textfield;
}

.el-input input[type='number']::-webkit-outer-spin-button,
.el-input input[type='number']::-webkit-inner-spin-button {
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
}

.el-input input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.el-dialog.dex-confirm-modal,
.el-dialog.dex-success-modal {
  margin: 70px auto;
  width: 482px;
  min-width: 482px;

  .el-dialog__body {
    padding-bottom: 14px;
  }
}

.el-dialog.dex-confirm-mobile,
.el-dialog.dex-success-mobile {
  margin-top: 0;
  width: unset;
  min-width: unset;
}

.el-dialog.dex-modal {
  margin: 70px auto 0;
  min-width: 560px;
  height: 520px;
  min-height: 520px;
  backdrop-filter: none;
  overflow: visible;
  z-index: 10;
  position: relative;
}

.el-dialog.dex-modal-mobile {
  box-shadow: none;
  border: none;
  overflow: visible;
  padding: 20px 8px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;

  .el-dialog__body {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
  }
}

.el-slider.slider-default {
  height: 16px;

  .el-slider__runway {
    height: 5px;
    background-color: var(--blue-15);
  }

  .el-slider__button {
    height: 16px;
    width: 16px;
    border: 1px solid rgba(30, 53, 62, 1);
    background: linear-gradient(180deg, #0083ad 0%, #00354f 100%);
    box-shadow: 2px 4px 5px 0px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
  }

  .el-slider__bar {
    background: transparent;
  }
}

.el-input.is-error {
  .el-input__inner {
    color: var(--error) !important;
    -webkit-text-fill-color: var(--error) !important;
  }
}

.fade-token-enter-from,
.fade-token-leave-to {
  opacity: 0;

  transform: translateX(-50%) scale(0.95) !important;
  transform-origin: center !important;
}
.fade-token-enter-to,
.fade-token-leave-from {
  opacity: 1;
  transform: translateX(-50%) scale(1);
}
.fade-token-enter-active,
.fade-token-leave-active {
  transform-origin: center !important;
  transition:
    opacity 0.3s ease-in-out,
    transform 0.3s ease-in-out;
}
</style>
